import { isValidPhoneNumber, parsePhoneNumberFromString, getCountryCallingCode } from 'libphonenumber-js/max';
import { Metadata } from 'libphonenumber-js/core';
import metadata from 'libphonenumber-js/max/metadata';

const sanitizeDialCode = (dialCode = '') => `${dialCode}`.replace(/\D/g, '');

const sanitizePhone = (phone = '') => `${phone}`.trim().replace(/(?!^\+)\D/g, '');

const sanitizeCountryCode = (countryCode = '') => `${countryCode}`.trim().toUpperCase();

export const normalizePhoneNumber = (phone, dialCode, countryCode) => {
    if (!phone) return '';

    const sanitizedPhone = sanitizePhone(phone);

    if (!sanitizedPhone) return '';

    if (sanitizedPhone.startsWith('+') && !sanitizedPhone.startsWith('+0')) {
        const parsedPhone = parsePhoneNumberFromString(sanitizedPhone);
        if (parsedPhone) {
            return parsedPhone.number;
        }
    }

    if (sanitizedPhone.startsWith('+0') && countryCode) {
        const nationalNumber = sanitizedPhone.slice(2); // Remove "+0"
        const parsedPhone = parsePhoneNumberFromString(nationalNumber, sanitizeCountryCode(countryCode));
        if (parsedPhone) {
            return parsedPhone.number;
        }
    }

    if (countryCode) {
        const parsedPhone = parsePhoneNumberFromString(sanitizedPhone.replace(/^\+0?/, ''), sanitizeCountryCode(countryCode));
        if (parsedPhone) {
            return parsedPhone.number;
        }
    }

    const sanitizedDialCode = sanitizeDialCode(dialCode);
    const nationalNumber = sanitizedPhone.replace(/\D/g, '').replace(/^0+/, '');

    if (!sanitizedDialCode || !nationalNumber) return '';

    return `+${sanitizedDialCode}${nationalNumber}`;
};

export const isValidInternationalPhoneNumber = (phone, dialCode, countryCode) => {
    const normalizedPhone = normalizePhoneNumber(phone, dialCode, countryCode);

    if (!normalizedPhone) return false;

    return isValidPhoneNumber(normalizedPhone);
};

const mobileRuleCache = {};

const getTrailingAnyDigitCount = (pattern = '') => {
    const exactCount = `${pattern}`.match(/\\d\{(\d+)\}$/);
    if (exactCount) return Number(exactCount[1]);
    return 0;
};

const getMobileRule = (countryCode) => {
    const iso = sanitizeCountryCode(countryCode);
    if (!iso) return null;
    if (Object.prototype.hasOwnProperty.call(mobileRuleCache, iso)) {
        return mobileRuleCache[iso];
    }

    const countryMetadata = new Metadata(metadata);
    if (!countryMetadata.hasCountry(iso)) {
        mobileRuleCache[iso] = null;
        return null;
    }

    countryMetadata.selectNumberingPlan(iso);
    const numberingPlan = countryMetadata.numberingPlan;
    const mobileType = numberingPlan.type('MOBILE');
    const pattern = mobileType && mobileType.pattern();

    let callingCode = '';
    try {
        callingCode = `${getCountryCallingCode(iso)}`;
    } catch (error) {
        callingCode = numberingPlan.callingCode() || '';
    }

    mobileRuleCache[iso] = {
        regex: pattern ? new RegExp(`^${pattern}$`) : null,
        lengths: (mobileType && mobileType.possibleLengths()) || numberingPlan.possibleLengths() || [],
        trailingAnyDigits: getTrailingAnyDigitCount(pattern),
        nationalPrefix: numberingPlan.nationalPrefix() || '',
        callingCode,
    };

    return mobileRuleCache[iso];
};

const canCompleteMobile = (nationalDigits, length, rule) => {
    if (!rule.regex || nationalDigits.length > length) return false;
    if (nationalDigits.length === length) return rule.regex.test(nationalDigits);

    const leadingLength = Math.max(0, length - rule.trailingAnyDigits);
    if (nationalDigits.length >= leadingLength) {
        return rule.regex.test(nationalDigits.padEnd(length, '0'));
    }

    if (leadingLength - nationalDigits.length > 3) return true;

    for (let digit = 0; digit <= 9; digit += 1) {
        if (canCompleteMobile(`${nationalDigits}${digit}`, length, rule)) {
            return true;
        }
    }

    return false;
};

const canBeMobilePrefix = (nationalDigits, rule) => {
    if (!rule?.regex) return true;
    const lengths = rule.lengths.length ? rule.lengths : [nationalDigits.length];
    return lengths.some((length) => canCompleteMobile(nationalDigits, length, rule));
};

const toLocalDigits = (phone, rule) => {
    let local = `${phone}`.replace(/\D/g, '');
    if (!local) return '';

    if (rule.callingCode && local.startsWith(rule.callingCode)) {
        local = local.slice(rule.callingCode.length);
    }
    if (rule.nationalPrefix && !local.startsWith(rule.nationalPrefix)) {
        local = `${rule.nationalPrefix}${local}`;
    }
    return local;
};

const getCountryDisplayName = (countryCode) => {
    try {
        return new Intl.DisplayNames(['en'], { type: 'region' }).of(countryCode) || countryCode;
    } catch (error) {
        return countryCode;
    }
};

export const getInvalidMobilePrefix = (phone, dialCode, countryCode) => {
    const iso = sanitizeCountryCode(countryCode);
    if (!phone || !iso) return null;

    const rule = getMobileRule(iso);
    if (!rule?.regex) return null;

    const local = toLocalDigits(phone, rule);
    const national = rule.nationalPrefix && local.startsWith(rule.nationalPrefix)
        ? local.slice(rule.nationalPrefix.length)
        : local;

    if (national.length < 2) return null;
    if (canBeMobilePrefix(national, rule)) return null;

    return {
        prefix: `${rule.nationalPrefix || ''}${national.slice(0, 2)}`,
        country: getCountryDisplayName(iso),
    };
};


export const buildMsisdn = (phone, dialCode, countryCode) => {
    return normalizePhoneNumber(phone, dialCode, countryCode);
};

export const toLocalPhoneNumber = (phone, dialCode, countryCode) => {
    const e164 = normalizePhoneNumber(phone, dialCode, countryCode) || phone;
    if (!e164) return phone || '';

    const parsed = parsePhoneNumberFromString(e164);
    if (parsed) {
        return parsed.formatNational().replace(/\D/g, '');
    }

    return e164;
};
